import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ModbusSlave} from '@models/adminstration/gateway/gateway-configuration/modbus-slave';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ModbusRegister} from '@models/adminstration/gateway/gateway-configuration/modbus-register';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MAX_NUMBER_OF_REGISTERS} from '@constants/administration/gateway/gateway-constants';
import {ModbusSignal} from '@models/adminstration/gateway/gateway-configuration/modbus-signal';

@Component({
    selector: 'scitis-admin-gateway-modbus-slave',
    templateUrl: './gateway-modbus-slave.component.html',
    styleUrls: ['./gateway-modbus-slave.component.scss'],
    standalone: false
})
export class GatewayModbusSlaveComponent implements OnInit, OnChanges {

  @Input()
  modbus: ModbusSlave = new ModbusSlave();

  @Input()
  signalNames: string[] = [];

  @Output()
  signalFormIsValid = new EventEmitter<boolean>();

  @Output()
  registerFormIsValid = new EventEmitter<boolean>();

  @Output()
  slaveFormIsValid = new EventEmitter<boolean>();

  @Output()
  removeSlaveChangeEvent = new EventEmitter<ModbusSlave>();

  @Output()
  removeSignalChangeEvent = new EventEmitter<ModbusSignal>();

  private COMPONENT_NAME: string = 'ModbusSlaveComponent';
  private oldModbusReadCode: number = 0;
  private oldModbusId: number = 0;
  private readonly maximumRegistersWarning: string = '';

  DEVICE_ID_MIN: number = 1;
  DEVICE_ID_MAX: number = 247;
  ADDRESS_READ_CODE_MIN: number = 3;
  ADDRESS_READ_CODE_MAX: number = 6;
  modbusSlaveForm: UntypedFormGroup = new UntypedFormGroup({});

  constructor(private notification: NotificationService,
              private translate: TranslateService) {
    this.maximumRegistersWarning = this.translate.instant('The maximum number of registers of "#" was reached').replace('#', MAX_NUMBER_OF_REGISTERS);
  }

  ngOnInit(): void {
    this.oldModbusReadCode = this.modbus.readCode;
    this.oldModbusId = this.modbus.deviceId;
    this.generateSlaveForm(this.modbus);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.modbus?.firstChange) {
      this.updateSlaveForm();
    }
  }

  get formControls() {
    return this.modbusSlaveForm.controls;
  }

  remove() {
    this.removeSlaveChangeEvent.emit(this.modbus);
  }

  addRegister() {
    if (this.modbus.registers.length >= MAX_NUMBER_OF_REGISTERS) {
      this.notification.warning(this.maximumRegistersWarning, this.COMPONENT_NAME);
      return;
    }
    let addressValue;
    if (!this.modbus.registers || this.modbus.registers.length <= 0) {
      this.modbus.registers = [];
      addressValue = 0;
    } else {
      addressValue = this.modbus.registers[this.modbus.registers.length - 1].address + 1;
    }
    const newRegister: ModbusRegister = {
      address: addressValue, signals: []
    };
    this.modbus.registers.push(newRegister);
  }

  onRegisterFormIsValid(valid: boolean) {
    this.registerFormIsValid.emit(valid);
  }

  onSignalFormIsValid(valid: boolean) {
    this.signalFormIsValid.emit(valid);
  }

  onRemoveRegister(register: ModbusRegister) {
    const index = this.modbus.registers.indexOf(register);
    if (index < 0) {
      return;
    }
    this.modbus.registers.splice(index, 1);
  }

  onRemoveSignal(signal: ModbusSignal) {
    this.removeSignalChangeEvent.emit(signal);
  }

  onUpdateSlaveProperties() {
    this.modbus.name = this.formControls.slaveName.value;
    this.modbus.deviceId = this.formControls.deviceId.value;
    this.modbus.readCode = this.formControls.addressCode.value;
    this.slaveFormIsValid.emit(!this.modbusSlaveForm.invalid);
  }

  private generateSlaveForm(modbus: ModbusSlave) {
    this.modbusSlaveForm = new UntypedFormGroup({
      slaveName: new UntypedFormControl(modbus.name, [Validators.required]),
      deviceId: new UntypedFormControl(modbus.deviceId, [Validators.required, Validators.min(this.DEVICE_ID_MIN), Validators.max(this.DEVICE_ID_MAX)]),
      addressCode: new UntypedFormControl(modbus.readCode, [Validators.required, Validators.min(this.ADDRESS_READ_CODE_MIN), Validators.max(this.ADDRESS_READ_CODE_MAX)])
    });
    this.slaveFormIsValid.emit(!this.modbusSlaveForm.invalid);
  }

  updateSlaveForm() {
    this.formControls.deviceId.setValue(this.modbus.deviceId);
    this.formControls.addressCode.setValue(this.modbus.readCode);
    this.slaveFormIsValid.emit(!this.modbusSlaveForm.invalid);
    this.modbus.registers = [...this.modbus.registers];
  }
}
